import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { ia, iaRa } from 'lib/helpers/utility';
import { useUserPreferences } from 'lib/hooks/queries/user/useUserPreferences';

import { DEFAULT_PAGE_SIZE } from 'components/shared/AgTablePage/lib/constants';

import { areArraysEqualUnordered, formatValue, updatePreferences } from '../helper';

export const useTablePreferences = ({ name, cols, preserveUserPreferences = true }) => {
  const [colDefs, setColDefs] = useState(cols);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [updatedPreferences, setUpdatedPreferences] = useState(false);
  const [isUserInitiated, setIsUserInitiated] = useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: preferencesData, isFetched: isPreferencesFetched } = useUserPreferences({
    params: { name },
    dependencies: [name],
    options: {
      enabled: Boolean(name?.length)
    }
  });

  const preferenceColumns = useMemo(
    () => preferencesData?.preferences?.value?.columnOrder,
    [preferencesData]
  );

  useEffect(() => {
    setLimit(preferencesData?.preferences?.value?.limit ?? DEFAULT_PAGE_SIZE);
  }, [preferencesData]);

  // Which columns, not how many: a table that swaps columns per view (a queue tab adding its own
  // column while dropping another) keeps its count, so a length check never sees the change and
  // the merge below stays short-circuited on the previous view's columns.
  const colsSignature = useMemo(
    () =>
      iaRa(cols)
        .map((col) => col?.field ?? col?.colId ?? '')
        .join('|'),
    [cols]
  );

  useEffect(() => {
    if (cols?.length) {
      setUpdatedPreferences(false);
    }
  }, [colsSignature]);

  useEffect(() => {
    if (updatedPreferences) return;

    if (!preferencesData) {
      setColDefs(cols);
      return;
    }

    if (ia(preferenceColumns)) {
      const areEqual = areArraysEqualUnordered(
        formatValue(cols)?.columnOrder,
        formatValue(preferenceColumns)?.columnOrder
      );

      if (!areEqual && (!preserveUserPreferences || isUserInitiated)) {
        setColDefs(cols);
        updatePreferences({ navigate, cols, limit, queryClient, name });
        setUpdatedPreferences(true);
        setIsUserInitiated(false);
        return;
      }

      const mappedColumns = Object.fromEntries(
        iaRa(preferenceColumns)
          .filter((col) => col?.field)
          .map((col) => [col.field, col])
      );
      const orderMap = Object.fromEntries(
        iaRa(preferenceColumns)
          .filter((col) => col?.field)
          .map((col, index) => [col.field, index])
      );

      const mergedColumns = iaRa(cols).map((col) => {
        const colPreference = mappedColumns[col?.field] || {};

        const colHidden = colPreference?.hide !== undefined ? colPreference?.hide : col?.hide;
        const colPinned = colPreference?.pinned !== undefined ? colPreference?.pinned : col?.pinned;
        const colRowGroup =
          colPreference?.rowGroup !== undefined ? colPreference?.rowGroup : col?.rowGroup;

        const hasSavedWidth = colPreference?.width != null;

        return {
          ...col,
          hide: colHidden,
          pinned: colPinned,
          rowGroup: colRowGroup,
          ...(hasSavedWidth && { width: colPreference.width, flex: null })
        };
      });

      const columns = mergedColumns
        .filter((col) => orderMap[col.field] !== undefined)
        .sort((a, b) => orderMap[a.field] - orderMap[b.field]);

      mergedColumns.forEach((col, index) => {
        if (orderMap[col.field] === undefined) {
          columns.splice(Math.min(index, columns.length), 0, col);
        }
      });

      setColDefs(columns);
      setUpdatedPreferences(true);
      return;
    }

    // Fetched, but this table has nothing saved — the endpoint answers { preferences: null }, which
    // is truthy, so neither branch above runs and colDefs would keep the set captured at mount.
    // With no preference to merge, the view's own columns are the answer.
    setColDefs(cols);
    setUpdatedPreferences(true);
  }, [
    preferencesData,
    preferenceColumns,
    cols,
    limit,
    name,
    navigate,
    queryClient,
    updatedPreferences,
    preserveUserPreferences,
    isUserInitiated
  ]);

  const persistColumns = useCallback(
    async (currentColumns) => {
      setColDefs(currentColumns);
      setIsUserInitiated(true);
      try {
        await updatePreferences({ navigate, cols: currentColumns, limit, queryClient, name });
      } catch (error) {
        setColDefs(cols);
      }
    },
    [limit, name, navigate, queryClient, cols]
  );

  const columnsReady = isPreferencesFetched && (updatedPreferences || !ia(preferenceColumns));

  return {
    colDefs,
    setColDefs,
    limit,
    setLimit,
    persistColumns,
    isPreferencesFetched,
    columnsReady
  };
};
